import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { NavLink } from "react-router-dom"
import { db } from "../../utils/firebase"
import { AuthContext } from "../../utils/providers/auth"
import firebase from "firebase"
import moment from "moment"
import "./assign-bookings.scss"

import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Table from "../../components/structure/table/table"
import Select from "../../components/ui/select/select"
import Datepicker from "../../components/ui/datepicker/datepicker"
import Button from "../../components/ui/button/button"
import { LoadingIcon } from "../../utils/svgs"

import AssignAgent from "./_components/assign-agent/assign-agent"

function ClientCell({ clientID, maskDetails }) {
	const [clientName, setClientName] = useState("")

	useEffect(() => {
		if (!clientID) return
		let cancelled = false

		db.doc(`clients/${clientID}`)
			.get()
			.then((clientDoc) => {
				if (cancelled || !clientDoc.exists) return
				const { first_name, last_name } = clientDoc.data() || {}
				setClientName(`${first_name || ""} ${last_name || ""}`.trim())
			})

		return () => {
			cancelled = true
		}
	}, [clientID])

	if (!clientID) return <small className="no-value">No client</small>

	if (maskDetails) return <>{clientName ? clientName.replace(/./g, "*") : "Loading..."}</>

	return <NavLink to={`client/${clientID}`}>{clientName || "Loading..."}</NavLink>
}

/**
 * Admin-only page for reassigning bookings from one sales agent to another.
 * Pick an agent + from-date, click Load, and reassign any booking to a new owner.
 * Reassignments flow through reassignBookingAgent so the full audit trail is recorded.
 */
export default function AssignBookings() {
	const { user } = useContext(AuthContext)

	const [agentsByUID, setAgentsByUID] = useState({})
	const [selectedAgent, setSelectedAgent] = useState("")
	const [fromDate, setFromDate] = useState(moment().startOf("day").valueOf())

	const [loading, setLoading] = useState(false)
	const [loaded, setLoaded] = useState(false)
	const [bookings, setBookings] = useState([])
	const [reassignBooking, setReassignBooking] = useState(null)

	const unsubscribeRef = useRef(null)

	useEffect(() => {
		db.collection("users")
			.orderBy("first_name")
			.get()
			.then((userDocs) => {
				const map = {}
				userDocs.forEach((userDoc) => {
					const { first_name, last_name } = userDoc.data()
					map[userDoc.id] = `${first_name || ""} ${last_name || ""}`.trim() || userDoc.id
				})
				setAgentsByUID(map)
			})
	}, [])

	useEffect(() => {
		return () => {
			if (unsubscribeRef.current) {
				unsubscribeRef.current()
				unsubscribeRef.current = null
			}
		}
	}, [])

	const loadBookings = () => {
		if (!selectedAgent || !fromDate) return

		if (unsubscribeRef.current) {
			unsubscribeRef.current()
			unsubscribeRef.current = null
		}

		setLoading(true)
		setLoaded(true)
		setBookings([])

		const fromTimestamp = firebase.firestore.Timestamp.fromMillis(moment(fromDate).startOf("day").valueOf())

		const query = db
			.collection("bookings")
			.where("agent", "==", selectedAgent)
			.where("removed", "==", false)
			.where("check_in", ">=", fromTimestamp)
			.orderBy("check_in", "asc")

		unsubscribeRef.current = query.onSnapshot(
			(snapshot) => {
				const rows = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
				setBookings(rows)
				setLoading(false)
			},
			(error) => {
				console.error("Error listening to bookings:", error)
				setLoading(false)
			}
		)
	}

	const bookingRows = useMemo(() => {
		return bookings.map((booking) => {
			const reference = booking.reference || ""
			const bookedLabel = booking.booked?.seconds ? moment(booking.booked.seconds, "X").format("MMM D[,] YYYY [at] HH:mm") : null
			const agentLabel = agentsByUID[booking.agent] || <small className="no-value">Unknown</small>
			const locationLabel = booking.booked_location || booking.enquiry?.location || <small className="no-value">-</small>
			const travelDateLabel = booking.check_in?.seconds ? moment(booking.check_in.seconds, "X").format("MMM D[,] YYYY") : null
			const groupSize = (Number(booking.golfers) || 0) + (Number(booking.non_golfers) || 0)
			const customerPrice = Number(booking.margins?.customer_price) || 0
			const profit = Number(booking.margins?.profit) || 0
			const agentsMargin = Number(booking.margins?.agents_margin) || 0

			return {
				id: booking.id,
				agent: booking.agent,
				reference,
				bookedLabel,
				agentLabel,
				clientID: booking.client,
				locationLabel,
				travelDateLabel,
				groupSize,
				customerPrice,
				profit,
				agentsMargin,
			}
		})
	}, [bookings, agentsByUID])

	return (
		<Tile fullPage={true}>
			<Title className="flex has-select-field">
				<h1>Assign bookings</h1>
			</Title>

			<div className="page-filters assign-bookings-filters">
				<Select
					label="Sales agent:"
					placeholder="Sales agent:"
					value={agentsByUID[selectedAgent]}
					activeOnHover={true}
					onSelect={(option) => setSelectedAgent(option.option)}
					options={agentsByUID}
				/>

				<Datepicker
					placeholder="From date:"
					value={fromDate}
					onSelect={(date) => setFromDate(date)}
					activeOnHover={true}
				/>

				<div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
					<Button
						filters={true}
						disabled={!selectedAgent || !fromDate}
						label="Load"
						loadingText="Loading..."
						loading={loading}
						onClick={() => loadBookings()}
					/>
				</div>
			</div>

			<Table
				className="bookings-table"
				headings={["Booking ref", "Booked on", "Sales agent", "Client", "Location", "Travel date", "Group size", "Booking value", "Sales margin", "Agent commission", ""]}
				noResults={loaded && !loading && bookingRows.length === 0}
				noResultsMessage={`No bookings found for ${agentsByUID[selectedAgent] || "this agent"} from ${moment(fromDate).format("MMM D[,] YYYY")} onwards.`}>
				{bookingRows.map((row) => (
					<tr key={row.id}>
						<td>
							{row.reference ? (
								<NavLink to={`booking/${row.id}`}>{row.reference}</NavLink>
							) : (
								<NavLink to={`booking/${row.id}`}>
									<small className="no-value">No reference</small>
								</NavLink>
							)}
						</td>
						<td>{row.bookedLabel || <small className="no-value">-</small>}</td>
						<td>{row.agentLabel}</td>
						<td>
							<ClientCell
								clientID={row.clientID}
								maskDetails={user?.mask_details}
							/>
						</td>
						<td>{row.locationLabel}</td>
						<td>{row.travelDateLabel || <small className="no-value">Not set</small>}</td>
						<td>{row.groupSize || <small className="no-value">-</small>}</td>
						<td>£{row.customerPrice.toLocaleString()}</td>
						<td>£{row.profit.toLocaleString()}</td>
						<td>£{row.agentsMargin.toLocaleString()}</td>
						<td className="is-button">
							<Button
								small={true}
								label="Assign"
								onClick={() =>
									setReassignBooking({
										bookingID: row.id,
										currentAgent: row.agent,
									})
								}
							/>
						</td>
					</tr>
				))}

				{loading && (
					<div className="table-loading-splash">
						<div className="loading-wrapper">
							<p>Loading...</p>
							<div className="svg-loading-wrap">
								<LoadingIcon />
							</div>
						</div>
					</div>
				)}
			</Table>

			{reassignBooking && (
				<AssignAgent
					bookingID={reassignBooking.bookingID}
					currentAgent={reassignBooking.currentAgent}
					agentsByUID={agentsByUID}
					close={() => setReassignBooking(null)}
					onAssigned={(reassignedID, newAgentID) => {
						if (newAgentID !== selectedAgent) {
							setBookings((prev) => prev.filter((booking) => booking.id !== reassignedID))
						}
					}}
				/>
			)}
		</Tile>
	)
}
